/**
 * Payment Success Page
 * 
 * Displayed when user returns from successful Stripe payment
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Icon from '../components/Icon';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const processPayment = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        // Get auth token
        const token = localStorage.getItem('therapy_auth_token');
        if (!token) {
          console.error('No auth token found');
          setLoading(false);
          return;
        }

        // First try to process payment with session ID
        let response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/stripe/process-successful-payment/${sessionId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        let data;
        
        if (!response.ok) {
          console.log('Session processing failed, trying fallback method...');
          
          // Fallback: Add credits based on plan type (assuming "prova" for now)
          // In a real scenario, you'd store the plan type in localStorage during payment
          const planType = localStorage.getItem('selectedPlanType') || 'prova';
          
          response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/stripe/add-credits-from-payment?plan_type=${planType}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          });
          
          if (!response.ok) {
            throw new Error(`Both methods failed: ${response.statusText}`);
          }
        }

        data = await response.json();
        setPaymentData({
          sessionId,
          status: 'succeeded',
          message: 'Din betalning har genomförts framgångsrikt!',
          creditsAdded: data.credits_added || data.credits_granted || 50, // fallback to 50 if not available
          newCredits: data.new_credits || data.credits_added
        });
        
        // Update user's credit balance in localStorage if it exists
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        if (currentUser.id) {
          currentUser.available_credits = data.new_credits || data.credits_added;
          localStorage.setItem('currentUser', JSON.stringify(currentUser));
        }
        
      } catch (error) {
        console.error('Error processing payment:', error);
        setPaymentData({
          sessionId,
          status: 'error', 
          message: 'Ett fel uppstod vid behandling av betalningen. Kontakta support om credits inte visas korrekt.'
        });
      } finally {
        setLoading(false);
      }
    };

    processPayment();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Bekräftar din betalning...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <Icon name="check-circle" size={32} className="text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Betalning Genomförd!
          </h1>
          <p className="text-gray-600 mb-4">
            {paymentData?.message}
          </p>
          {paymentData?.creditsAdded && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-800 font-semibold">
                💎 Nya credits tillagda: {paymentData.creditsAdded}
              </p>
              <p className="text-green-600 text-sm">
                Totalt har du nu {paymentData.newCredits} credits för AI-terapi chattar!
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Link 
            to="/client/chat"
            className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Starta AI-terapi chatten
          </Link>
          
          <Link 
            to="/profile"
            className="inline-flex items-center justify-center px-4 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Visa Kontoinställningar
          </Link>
        </div>

        <div className="mt-6 text-xs text-gray-500">
          <p>Dina credits har lagts till på ditt konto automatiskt.</p>
          <p className="mt-1">Du kommer få en bekräftelse via e-post inom kort.</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
